import type { NavigationItem } from '@src/tokens/navigationPaths';
import {
  Icon,
  IconName,
  MixpanelEventSource,
  MixpanelSourceProvider,
  MOBILE_HEADER_TITLE_PORTAL,
  Portal,
  StackedTalosLogo,
  useMobileModal,
} from '@talos/kyoko';
import { useCallback, useMemo } from 'react';
import { matchRoutes, useLocation } from 'react-router-dom';
import { useUser } from '../../../hooks';
import { useNavigationItems } from '../../../hooks/useNavigationItems';
import { MobileNavMenuSheet } from './MobileNavMenuSheet';
import { MobileNavMenuButton, MobileNavMenuLabel } from './styles';

export function MobileNavMenu() {
  const { navItems, topNavItems } = useNavigationItems();
  const user = useUser();
  const location = useLocation();
  const { openModal, closeModal, isModalOpen } = useMobileModal();
  const isOrgMorphed = user?.UsingOrgID != null;

  const matches = useMemo(() => matchRoutes(navItems, location), [location, navItems]);
  const currentSection = matches?.at(0)?.route;
  const currentLabel = getCurrentLeafLabel(matches?.map(match => match.route) ?? []) ?? currentSection?.label;

  const handleOpenMenu = useCallback(() => {
    if (isModalOpen) {
      closeModal();
      return;
    }

    openModal({
      content: (
        <MixpanelSourceProvider value={MixpanelEventSource.MobileNavMenu}>
          <MobileNavMenuSheet currentSection={currentSection} topNavItems={topNavItems} closeModal={closeModal} />
        </MixpanelSourceProvider>
      ),
    });
  }, [closeModal, currentSection, isModalOpen, openModal, topNavItems]);

  if (currentSection == null || currentLabel == null) {
    return null;
  }

  return (
    <Portal portalId={MOBILE_HEADER_TITLE_PORTAL}>
      <MobileNavMenuButton
        aria-label="Open navigation menu"
        data-testid="mobile-nav-menu-button"
        onClick={handleOpenMenu}
        background={isOrgMorphed ? 'colors.orange.dim' : undefined}
      >
        <StackedTalosLogo data-testid="mobile-nav-menu-logo" />
        <MobileNavMenuLabel data-testid="mobile-nav-menu-label">{currentLabel}</MobileNavMenuLabel>
        <Icon icon={IconName.ChevronDown} size={16} />
      </MobileNavMenuButton>
    </Portal>
  );
}

function getCurrentLeafLabel(routes: NavigationItem[]) {
  return routes
    .toReversed()
    .map(route => route.label)
    .find((label): label is string => label != null);
}
